'use es6';

import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import Section from 'LayoutBuilderUI/components/Section';
import SectionList from 'LayoutBuilderUI/components/SectionList';
import EmptyTemplatePlaceholder from 'LayoutBuilderUI/components/EmptyTemplatePlaceholder';
import HeaderSection from './HeaderSection';
import FooterSection from './FooterSection';
import HubspotBrandingSection from './HubspotBrandingSection';
import classNames from 'classnames';
import { floatingAlertMessage } from 'LayoutBuilderUI/actions/ApiActions';
import { getScrollbarSize } from 'EmailEditorUI/js/utils/scrollbarSizeUtil';
import { clearSelectedText } from 'ContentEditorUI/utils/helpers';
import AsyncOnboardingContainer from 'LayoutBuilderUI/containers/AsyncOnboardingContainer';
import ClickCatcher from 'LayoutBuilderUI/containers/ClickCatcher';
import { RESIZE_HANDLE } from 'LayoutBuilderUI/lib/DragDropConstants';
import { SCROLL_CONTAINER_ID } from 'LayoutBuilderUI/utils/scrollPositionUtils';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { NamespaceWrapper } from 'EmailEditorUI/components/NamespaceWrapper';
import { getActiveBreakpointName } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import SectionCommentableArea from 'LayoutBuilderUI/components/utility/SectionCommentableArea';
import BreakpointContextProvider from 'EmailEditorUI/components/mo/BreakpointContext';
import debounce from 'react-utils/debounce';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class LayoutBuilder extends Component {
  constructor(props) {
    super(props);
    this.onMouseUp = () => {
      setTimeout(() => {
        if (this.props.selectedType !== 'ELEMENT') {
          clearSelectedText();
        }
      }, 0);
    };
    this.onDragOver = e => {
      this.props.maybeUpdateScrollOnDrag(e);
    };
    this.onDragLeave = e => {
      this.dragCounter--;
      if (e.target.className === 'selected-click-catcher') {
        this.dragCounter = 0;
      }
      if (this.dragCounter === 0 && this.state.isDragging) {
        this.setState({
          isDragging: false,
          isDraggingDisallowedFileType: false
        });
      }
    };
    this.onDragEnter = e => {
      this.dragCounter++;
      if (!this.state.isDragging && e.dataTransfer.items && e.dataTransfer.items.length && e.dataTransfer.items[0].kind === 'file') {
        const fileType = e.dataTransfer.items[0].type;
        const fileTypeAllowed = this.props.hasHubspotVideoAccess && fileType.indexOf('video') === 0 || fileType.indexOf('image') === 0 && fileType.indexOf('svg') === -1;
        if (!fileTypeAllowed && !this.state.isDraggingDisallowedFileType) {
          floatingAlertMessage({
            type: 'danger',
            titleText: I18n.text('fileUpload.invalid.title'),
            message: fileType.startsWith('image') || !this.props.hasHubspotVideoAccess ? I18n.text('fileUpload.invalid.imageMessage') : I18n.text('fileUpload.invalid.message')
          });
        }
        this.setState({
          isDragging: true,
          isDraggingDisallowedFileType: !fileTypeAllowed
        });
        if (fileTypeAllowed && e.target.className === 'selected-click-catcher') {
          this.dragCounter--;
        }
      }
    };
    this.onDrop = () => {
      if (this.state.isDragging) {
        this.dragCounter = 0;
        this.setState({
          isDragging: false,
          isDraggingDisallowedFileType: false
        });
      }
    };
    this.manageScrollContainerRef = ref => {
      this._ref = ref;
      this.props.scrollContainerRef(ref);
      if (ref) {
        this.debouncedScroll();
      }
    };
    this.scrollContainerWithBoundaries = () => {
      if (!this._ref) return;
      this.props.scrollContainer({
        top: 0,
        bottom: this._ref.offsetHeight,
        scrollTop: this._ref.scrollTop,
        scrollHeight: this._ref.scrollHeight,
        clientHeight: this._ref.clientHeight
      });
    };
    this.onScroll = () => {
      this.debouncedScroll();
    };
    this.renderSection = (id, index) => {
      return /*#__PURE__*/_jsx(SectionCommentableArea, {
        sectionId: id,
        children: /*#__PURE__*/_jsx(Section, {
          draggable: true,
          index: index,
          sectionId: id,
          scrollContainerWithBoundaries: this.scrollContainerWithBoundaries
        })
      }, id);
    };
    this.renderSections = () => {
      const {
        sections,
        hasVisibleSectionsForActiveBreakpoint
      } = this.props;
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SectionList, {
          children: this.renderSection
        }), !hasVisibleSectionsForActiveBreakpoint && /*#__PURE__*/_jsx(EmptyTemplatePlaceholder, {
          isEmpty: sections.size === 0
        })]
      });
    };
    this.renderFooter = () => {
      const {
        sections,
        showHubspotBranding
      } = this.props;
      return /*#__PURE__*/_jsx(FooterSection, {
        lastSection: sections.last(),
        numSections: sections.size,
        showHubspotBranding: showHubspotBranding
      });
    };
    this.renderHubspotBrandingSection = () => {
      const {
        sections
      } = this.props;
      return /*#__PURE__*/_jsx(HubspotBrandingSection, {
        numSections: sections.size,
        lastSection: sections.last()
      });
    };
    this.state = {
      isDragging: false,
      isDraggingDisallowedFileType: false
    };
    this.dragCounter = 0;
    this.shouldRenderClickCatcher = this.shouldRenderClickCatcher.bind(this);
    this.debouncedScroll = debounce(this.scrollContainerWithBoundaries, 50);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.sections.size < this.props.sections.size) {
      const newSectionIndex = this.props.sections.findIndex(s => prevProps.sections.indexOf(s) < 0);
      if (newSectionIndex > -1) {
        this.props.selectSectionIfEmpty(newSectionIndex);
      }
    }
  }
  componentWillUnmount() {
    if (this._handlerTimeout) {
      clearTimeout(this._handlerTimeout);
    }
  }
  shouldRenderClickCatcher() {
    const {
      selectedType,
      isReadOnlyMode
    } = this.props;
    const {
      isDragging,
      isDraggingDisallowedFileType
    } = this.state;
    if (isReadOnlyMode) {
      return true;
    }
    return selectedType && !isDragging || isDraggingDisallowedFileType;
  }
  renderHeader() {
    const {
      sections
    } = this.props;
    return /*#__PURE__*/_jsx(HeaderSection, {
      firstSection: sections.first(),
      numSections: sections.size
    });
  }
  renderFixedHeader() {
    const offset = getScrollbarSize();
    return /*#__PURE__*/_jsx("div", {
      style: {
        position: 'fixed',
        width: `calc(100% - ${offset}px)`,
        zIndex: 65534
      },
      children: /*#__PURE__*/_jsx("div", {
        id: "tinymce-toolbar",
        style: {
          position: 'fixed',
          width: `calc(100% - ${offset}px)`,
          zIndex: 65534
        }
      })
    });
  }
  renderLoading() {
    if (!this.props.showLoading) return null;
    return /*#__PURE__*/_jsx(NamespaceWrapper, {
      children: /*#__PURE__*/_jsx(LoadingOverlay, {
        grow: true,
        layout: "centered"
      })
    });
  }
  render() {
    const {
      activeBreakpointName,
      showHubspotBranding,
      isDraggingWithinApp,
      itemType
    } = this.props;
    const isResizing = isDraggingWithinApp && itemType === RESIZE_HANDLE;
    const shouldRenderClickCatcher = this.shouldRenderClickCatcher();
    const className = classNames((this.state.isDragging || isDraggingWithinApp) && "isDragging", this.state.isDraggingDisallowedFileType && "dropNotAllowed", !shouldRenderClickCatcher && "noClickCatcher");
    return /*#__PURE__*/_jsxs(BreakpointContextProvider, {
      breakpointName: activeBreakpointName,
      children: [this.renderLoading(), /*#__PURE__*/_jsxs("div", {
        id: SCROLL_CONTAINER_ID,
        className: className,
        onScroll: this.onScroll,
        ref: this.manageScrollContainerRef,
        onDragOver: this.onDragOver,
        onDrop: this.onDrop,
        onDragEnter: this.onDragEnter,
        onDragLeave: this.onDragLeave,
        "data-commenting-scroll-container": true,
        children: [/*#__PURE__*/_jsx(AsyncOnboardingContainer, {
          shouldHideShepherds: isResizing
        }), this.renderFixedHeader(), /*#__PURE__*/_jsxs("div", {
          id: "cos_layout_builder",
          onMouseUp: this.onMouseUp,
          children: [this.renderHeader(), this.renderSections(), !showHubspotBranding && this.renderFooter(), showHubspotBranding && this.renderHubspotBrandingSection(), /*#__PURE__*/_jsx(ClickCatcher, {
            shouldShowClickCatcher: shouldRenderClickCatcher
          })]
        })]
      })]
    });
  }
}
LayoutBuilder.propTypes = {
  activeBreakpointName: PropTypes.string.isRequired,
  sections: PropTypes.object.isRequired,
  hasVisibleSectionsForActiveBreakpoint: PropTypes.bool.isRequired,
  selectedType: PropTypes.string,
  maybeUpdateScrollOnDrag: PropTypes.func,
  scrollContainerRef: PropTypes.func,
  showHubspotBranding: PropTypes.bool,
  isReadOnlyMode: PropTypes.bool.isRequired,
  selectSectionIfEmpty: PropTypes.func.isRequired,
  isDraggingWithinApp: PropTypes.bool.isRequired,
  itemType: PropTypes.string,
  scrollContainer: PropTypes.func.isRequired,
  hasHubspotVideoAccess: PropTypes.bool.isRequired,
  showLoading: PropTypes.bool.isRequired
};
const mapStateToProps = state => ({
  activeBreakpointName: getActiveBreakpointName(state)
});
export default connect(mapStateToProps)(LayoutBuilder);